import { useSyncExternalStore } from 'react'
import {
  applyDocumentLang,
  getGuestLocale,
  isGuestLocale,
  subscribeGuestLocale,
  type GuestLocale,
} from '@/lib/guestLocale'
import { useAuthStore } from '@/store/authStore'
import { useSettingsStore } from '@/store/settingsStore'
import { getMinCopy } from '@/i18n/min'
import type { MinCopy, UiLocale } from '@/i18n/types'

export type { UiLocale, MinCopy }

/**
 * Interface locale for 44A-min.
 * Authenticated: settings.uiLanguage when supported; else guest locale.
 * Guest / preferGuest: always guest (landing continuity through auth + welcome).
 */
export function useUiLocale(options?: { preferGuest?: boolean }): UiLocale {
  const guest = useSyncExternalStore(subscribeGuestLocale, getGuestLocale, () => 'en' as GuestLocale)
  const token = useAuthStore((s) => s.accessToken)
  const uiLanguage = useSettingsStore((s) => s.settings?.uiLanguage)

  if (options?.preferGuest) return guest
  if (token && isGuestLocale(uiLanguage)) return uiLanguage
  return guest
}

export function useMinCopy(options?: { preferGuest?: boolean }): MinCopy {
  return getMinCopy(useUiLocale(options))
}

/** Apply html lang from settings after load (authenticated shell). */
export function syncDocumentLangFromSettings(uiLanguage: string | undefined): void {
  if (isGuestLocale(uiLanguage)) {
    applyDocumentLang(uiLanguage)
  }
}
